import { useId, useMemo, useState } from 'react';
import {
  apiErrorText,
  apiLoadErrorText,
  useImportEconUnitRatesFromSmetaMutation,
  useListEconCostItemLinesQuery,
  useListEconDictQuery,
  useListPtoProjectsQuery,
} from '@shared/api';
import { pluralizeRu } from '@shared/lib/formatFile';
import { Button } from '@shared/ui/Button';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconPlus } from '@shared/ui/icons';
import { CheckLabel, Control, Notice, TextButton, typo } from '@entities/fleet';
import { DictDrawer } from './DictDrawer';
import { DictTable } from './DictTable';
import { EmptyCard, FlashBox, Toolbar } from './styles';
import {
  DICT_UI,
  searchKey,
  searchText,
  shortGroup,
  type ColumnContext,
  type DictRecord,
  type LineOption,
  type TableDict,
} from './types';

const RECORDS = ['запись', 'записи', 'записей'] as const;

type Opened = { record: DictRecord | null } | null;

/**
 * Справочник: сверху поиск, «показать выключенные» и «Добавить …», ниже —
 * таблица записей. Запись открывается и заводится в боковой панели — форма
 * больше не висит под списком.
 */
export function DictPanel({ dict, canWrite }: { dict: TableDict; canWrite: boolean }) {
  const uid = useId();
  const ui = DICT_UI[dict];
  const { data, isLoading, error, refetch, isFetching } = useListEconDictQuery({
    dict,
    active: false,
  });
  const { data: projectsData } = useListPtoProjectsQuery(undefined, {
    skip: dict !== 'objects',
  });
  const { data: linesData } = useListEconCostItemLinesQuery(undefined, {
    skip: dict !== 'cost_items_1c',
  });
  const [importFromSmeta, importState] = useImportEconUnitRatesFromSmetaMutation();
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [opened, setOpened] = useState<Opened>(null);
  const [flash, setFlash] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  const items = useMemo(() => (data?.items ?? []) as DictRecord[], [data]);
  const inactiveCount = items.filter((r) => r.is_active !== true).length;
  const key = searchKey(query);
  const visible = useMemo(
    () =>
      items.filter(
        (r) => (showInactive || r.is_active === true) && (!key || searchText(r).includes(key)),
      ),
    [items, showInactive, key],
  );

  const projects = useMemo(() => projectsData?.items ?? [], [projectsData]);
  const projectOptions = useMemo(
    () =>
      projects.map((p) => ({
        value: p.id,
        label: p.code ? `${p.name} (${p.code})` : p.name,
      })),
    [projects],
  );
  const lineOptions = useMemo<LineOption[]>(
    () =>
      (linesData?.items ?? []).map((l) => ({
        value: l.id,
        label: l.name,
        group: shortGroup(l.group_name),
        distribution: l.distribution_note,
      })),
    [linesData],
  );
  const ctx = useMemo<ColumnContext>(
    () => ({
      projectName: (id) => projects.find((p) => p.id === id)?.name ?? null,
    }),
    [projects],
  );

  async function onImport() {
    setFlash(null);
    try {
      const res = await importFromSmeta().unwrap();
      setFlash({
        tone: 'success',
        text: `Импортировано единичных расценок из ЛСР и смет Сметчика: ${res.imported_count}.`,
      });
    } catch (err) {
      setFlash({ tone: 'danger', text: apiErrorText(err, 'Не удалось импортировать расценки.') });
    }
  }

  const addButton = canWrite && (
    <Button type="button" $variant="primary" onClick={() => setOpened({ record: null })}>
      <IconPlus size={16} />
      Добавить {ui.accusative}
    </Button>
  );

  return (
    <>
      <Toolbar>
        {items.length > 0 && (
          <Control
            className="search"
            type="search"
            value={query}
            placeholder={ui.searchPlaceholder ?? 'Найти по коду или названию'}
            aria-label={`Поиск: ${ui.accusative}`}
            onChange={(e) => setQuery(e.target.value)}
          />
        )}
        {inactiveCount > 0 && (
          <CheckLabel>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Выключенные ({inactiveCount})
          </CheckLabel>
        )}
        {items.length > 0 && (
          <p className="count" aria-live="polite">
            {visible.length === items.length
              ? `${items.length} ${pluralizeRu(items.length, RECORDS)}`
              : `${visible.length} из ${items.length}`}
          </p>
        )}
        <span className="spacer" />
        {dict === 'unit_rates' && canWrite && (
          <Button
            type="button"
            $variant="secondary"
            disabled={importState.isLoading}
            onClick={() => void onImport()}
          >
            {importState.isLoading ? 'Импорт из Сметчика…' : 'Импорт из Сметчика'}
          </Button>
        )}
        {items.length > 0 && addButton}
      </Toolbar>

      {flash && (
        <FlashBox>
          <Notice $tone={flash.tone} role={flash.tone === 'danger' ? 'alert' : 'status'}>
            <span className="text">{typo(flash.text)}</span>
            <TextButton type="button" onClick={() => setFlash(null)}>
              Скрыть
            </TextButton>
          </Notice>
        </FlashBox>
      )}

      {isLoading ? (
        <div aria-busy="true" aria-label="Загрузка справочника">
          <SkeletonLine $height={40} />
          {Array.from({ length: 5 }, (_, i) => (
            <SkeletonLine key={i} $height={20} $width={i % 2 === 0 ? '100%' : '70%'} />
          ))}
        </div>
      ) : error !== undefined && !data ? (
        <Notice $tone="danger" role="alert">
          <span className="text">
            {apiLoadErrorText(
              error,
              'Справочник не загрузился.',
              'Нет доступа к справочникам Экономиста.',
            )}
          </span>
          <TextButton type="button" disabled={isFetching} onClick={() => void refetch()}>
            Повторить
          </TextButton>
        </Notice>
      ) : items.length === 0 ? (
        <EmptyCard aria-labelledby={`${uid}-empty`}>
          <h3 id={`${uid}-empty`}>Справочник пока пуст</h3>
          <p>{typo(ui.emptyText)}</p>
          {canWrite && <div className="actions">{addButton}</div>}
        </EmptyCard>
      ) : visible.length === 0 ? (
        <EmptyCard>
          <h3>Ничего не нашлось</h3>
          <p>
            {typo(
              key
                ? `По запросу «${query.trim()}» записей нет${!showInactive && inactiveCount > 0 ? ' среди действующих — посмотрите и выключенные' : ''}.`
                : 'Все записи выключены — отметьте «Выключенные», чтобы их увидеть.',
            )}
          </p>
          <div className="actions">
            {key && (
              <Button type="button" $variant="secondary" onClick={() => setQuery('')}>
                Сбросить поиск
              </Button>
            )}
          </div>
        </EmptyCard>
      ) : (
        <DictTable
          dict={dict}
          rows={visible}
          ctx={ctx}
          onOpen={(record) => setOpened({ record })}
        />
      )}

      {opened && (
        <DictDrawer
          key={opened.record ? String(opened.record.id) : 'new'}
          dict={dict}
          record={opened.record}
          canWrite={canWrite}
          projectOptions={projectOptions}
          lineOptions={lineOptions}
          onClose={() => setOpened(null)}
          onSaved={(text) => {
            setOpened(null);
            setFlash({ tone: 'success', text });
          }}
        />
      )}
    </>
  );
}
